// A KEZDŐLAP (Áttekintés) csempe-katalógusa — EGYETLEN forrás a webnek (renderelés, galéria, alapértelmezés) ÉS a
// BFF-nek (a mentett elrendezés id-inek / méreteinek validálása). A spec: a kezdőlap-testreszabás terve (A-25).
//
// MIÉRT A `@atlas/shared`-BEN: a kapu (`gate`) két helyen számít — a web nem rajzolja ki a jog nélküli csempét, a BFF
// pedig a csempe ADAT-végpontján kapuz —, és a két oldalnak UGYANAZT kell tudnia. A kapu-kiértékelés tiszta függvény
// (`tileAllowed`), a `can` a hívó jog-ellenőrzője (web: `usePermissions().can`, BFF: `canPermission(claims, …)`).
//
// AZ ELRENDEZÉS = rendezett lista. A rejtett csempe az, amelyik NINCS a listában (nincs külön `hidden` mező).
// A jog nélküli, de ismert id a mentett listában MEGMARAD (jog-visszaadáskor visszajön a helyére) — ezt a
// `mergeHidden` őrzi mentéskor; az ismeretlen (kivezetett) id-t eldobja.
import { FINANCE_PERMISSIONS } from './finance-permissions.js';
/** Minden csempe-kiadásnál +1 — a „mit látott utoljára" jelzője (`since` > mentett `catalogRev` → „Új"). */
export const DASHBOARD_CATALOG_REV = 1;
/** A mentett elrendezés felső korlátja (a katalógus ennél jóval kisebb; a korlát a rosszindulatú body ellen való). */
export const DASHBOARD_MAX_TILES = 50;
/** Az „Ünnepeltek" csempe állítható ablaka (nap) — a `tiles[].opts.windowDays`-ben tárolva. */
export const DASHBOARD_WINDOW_DAYS = [7, 14, 30];
export const DASHBOARD_DEFAULT_WINDOW_DAYS = 14;
export const DASHBOARD_TILE_IDS = [
    'pinnedMessages',
    'kpi.clients',
    'kpi.accounts',
    'kpi.openTickets',
    'kpi.mrr',
    'finance',
    'meetings',
    'celebrations',
    'attention',
    'myTickets',
    'notes',
];
function tile(id, i18nKey, def) {
    return {
        id,
        titleKey: `dashboard.tiles.${i18nKey}.title`,
        descriptionKey: `dashboard.tiles.${i18nKey}.description`,
        ...def,
    };
}
const STAT = {
    kind: 'stat',
    defaultSize: 1,
    allowedSizes: [1],
    since: 0,
    removable: true,
    emptyBehaviour: 'show',
};
const PANEL = {
    kind: 'panel',
    defaultSize: 1,
    allowedSizes: [1, 2],
    removable: true,
    emptyBehaviour: 'show',
};
/** A katalógus. A SORREND a galéria alap-sorrendje; az alap-elrendezés a `defaultOrder`-ből jön. */
export const DASHBOARD_TILES = [
    // KÖTELEZŐ csempe (nem levehető, nincs a galériában): a kitűzött közlemény; tétel nélkül nézet-módban nem renderel.
    tile('pinnedMessages', 'pinnedMessages', {
        ...PANEL,
        category: 'work',
        gate: { all: ['messages.view'] },
        defaultSize: 2,
        defaultOrder: 0,
        removable: false,
        since: 0,
        emptyBehaviour: 'hide',
        icon: 'BookmarkIcon',
    }),
    tile('kpi.clients', 'kpiClients', {
        ...STAT,
        category: 'kpi',
        gate: { all: ['clients.view'] },
        defaultOrder: 10,
        icon: 'BuildingOffice2Icon',
    }),
    tile('kpi.accounts', 'kpiAccounts', {
        ...STAT,
        category: 'kpi',
        gate: { all: ['accounts.view'] },
        defaultOrder: 20,
        icon: 'UsersIcon',
    }),
    tile('kpi.openTickets', 'kpiOpenTickets', {
        ...STAT,
        category: 'kpi',
        gate: { all: ['tickets.view'] },
        defaultOrder: 30,
        icon: 'TicketIcon',
    }),
    tile('kpi.mrr', 'kpiMrr', {
        ...STAT,
        category: 'kpi',
        // védett jog (per-user grant): a `finance.revenue.view` BÁRMELYIKE
        gate: { any: FINANCE_PERMISSIONS.revenueView },
        defaultOrder: 40,
        icon: 'BanknotesIcon',
    }),
    tile('finance', 'finance', {
        ...PANEL,
        category: 'finance',
        gate: { all: ['invoices.view'] },
        defaultOrder: 50,
        since: 0,
        icon: 'ReceiptPercentIcon',
    }),
    tile('meetings', 'meetings', {
        ...PANEL,
        category: 'work',
        gate: { all: ['meetings.view'] },
        defaultOrder: 60,
        since: 0,
        icon: 'CalendarDaysIcon',
    }),
    tile('celebrations', 'celebrations', {
        ...PANEL,
        category: 'team',
        gate: { all: ['celebrations.view'] },
        defaultOrder: 70,
        since: 1,
        icon: 'CakeIcon',
    }),
    tile('attention', 'attention', {
        ...PANEL,
        category: 'work',
        gate: {
            any: ['payment_cards.view', 'usage_data.view', 'service_assignments.view', 'invoices.view'],
        },
        defaultOrder: 80,
        since: 0,
        emptyBehaviour: 'hide',
        icon: 'ExclamationTriangleIcon',
    }),
    tile('myTickets', 'myTickets', {
        ...PANEL,
        category: 'work',
        gate: { all: ['tickets.view'] },
        defaultOrder: null,
        since: 1,
        icon: 'InboxStackIcon',
    }),
    tile('notes', 'notes', {
        ...PANEL,
        category: 'personal',
        gate: {},
        defaultOrder: null,
        since: 1,
        icon: 'PencilSquareIcon',
    }),
];
const BY_ID = new Map(DASHBOARD_TILES.map((t) => [t.id, t]));
/** Egy csempe katalógus-bejegyzése — ismeretlen (kivezetett / hibás) id-re `undefined`. */
export function dashboardTileDef(id) {
    return BY_ID.get(id);
}
export function isDashboardTileId(id) {
    return BY_ID.has(id);
}
/** A kapu kiértékelése: a `can` a hívó jog-ellenőrzője (a `platform:admin` bypass a `can`-ban van). */
export function tileAllowed(def, can) {
    const all = def.gate.all ?? [];
    const any = def.gate.any ?? [];
    return all.every((p) => can(p)) && (any.length === 0 || any.some((p) => can(p)));
}
//# sourceMappingURL=dashboard-tiles.js.map